import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Avatar,
  Chip,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Tab,
  Tabs,
  Paper,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Grid,
  Stack,
} from '@mui/material';
import {
  Message,
  Reply,
  Schedule,
  Notifications,
  PriorityHigh,
  Person,
  School,
  Add,
  MarkEmailRead,
  AccessTime,
  Group,
} from '@mui/icons-material';

interface MessageThread {
  id: number;
  parentName: string;
  parentEmail: string;
  childName: string;
  subject: string;
  lastMessage: string;
  priority: 'normal' | 'high' | 'urgent';
  unreadCount: number;
  lastActivity: string;
  status: 'active' | 'resolved' | 'pending';
}

interface Meeting {
  id: number;
  parentName: string;
  childName: string;
  requestedDate: string;
  status: 'pending' | 'confirmed' | 'completed';
  topic: string;
  duration: string;
}

interface Announcement {
  id: number;
  title: string;
  content: string;
  targetAudience: string;
  priority: 'normal' | 'high';
  scheduledDate: string;
  status: 'draft' | 'scheduled' | 'sent';
}

const TeacherMessages: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [messageThreads, setMessageThreads] = useState<MessageThread[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);

  useEffect(() => {
    // Mock data - replace with API calls
    setLoading(true);
    setTimeout(() => {
      const mockMessageThreads: MessageThread[] = [
        {
          id: 1,
          parentName: 'Jennifer Johnson',
          parentEmail: 'jennifer.johnson@email.com',
          childName: 'Emma Johnson',
          subject: 'Mathematics - Homework Concerns',
          lastMessage: 'Emma seems to be struggling with the algebra homework. Could we schedule a meeting?',
          priority: 'high',
          unreadCount: 2,
          lastActivity: '2 hours ago',
          status: 'active'
        },
        {
          id: 2,
          parentName: 'Michael Davis',
          parentEmail: 'michael.davis@email.com',
          childName: 'Alex Davis',
          subject: 'Great Progress in Class!',
          lastMessage: 'Thank you for the positive feedback about Alex\'s participation.',
          priority: 'normal',
          unreadCount: 0,
          lastActivity: '1 day ago',
          status: 'resolved'
        },
        {
          id: 3,
          parentName: 'Sarah Williams',
          parentEmail: 'sarah.williams@email.com',
          childName: 'Olivia Williams',
          subject: 'Assignment Extension Request',
          lastMessage: 'Due to family emergency, requesting extension for the science project.',
          priority: 'urgent',
          unreadCount: 1,
          lastActivity: '30 minutes ago',
          status: 'pending'
        }
      ];

      const mockMeetings: Meeting[] = [
        {
          id: 1,
          parentName: 'Jennifer Johnson',
          childName: 'Emma Johnson',
          requestedDate: '2024-01-15 14:00',
          status: 'pending',
          topic: 'Mathematics Progress Discussion',
          duration: '30 minutes'
        },
        {
          id: 2,
          parentName: 'Robert Chen',
          childName: 'Kevin Chen',
          requestedDate: '2024-01-12 15:30',
          status: 'confirmed',
          topic: 'Behavioral Concerns',
          duration: '45 minutes'
        }
      ];

      const mockAnnouncements: Announcement[] = [
        {
          id: 1,
          title: 'Upcoming Parent-Teacher Conference',
          content: 'Individual conferences scheduled for next week. Please check your calendar.',
          targetAudience: 'All Parents',
          priority: 'high',
          scheduledDate: '2024-01-10',
          status: 'sent'
        },
        {
          id: 2,
          title: 'Mathematics Competition Results',
          content: 'Congratulations to all students who participated in the regional math competition.',
          targetAudience: 'Mathematics Students',
          priority: 'normal',
          scheduledDate: '2024-01-08',
          status: 'draft'
        }
      ];

      setMessageThreads(mockMessageThreads);
      setMeetings(mockMeetings);
      setAnnouncements(mockAnnouncements);
      setLoading(false);
    }, 1000);
  }, []);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'primary';
      case 'resolved':
        return 'success';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleThreadClick = (threadId: number) => {
    navigate(`/teacher/messages/thread/${threadId}`);
  };

  const totalUnreadMessages = messageThreads.reduce((sum, thread) => sum + thread.unreadCount, 0);
  const pendingMeetings = meetings.filter(m => m.status === 'pending').length;
  const draftAnnouncements = announcements.filter(a => a.status === 'draft').length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/teacher-dashboard')}
            sx={{ cursor: 'pointer' }}
          >
            Teacher Dashboard
          </Link>
          <Typography color="text.primary">Messages</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Teacher Messages
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage parent communications and meetings
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => navigate('/teacher/messages/compose')}
          >
            New Message
          </Button>
          <Button variant="outlined" startIcon={<Schedule />}>
            Schedule Meeting
          </Button>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <Badge badgeContent={totalUnreadMessages} color="error">
                    <Message />
                  </Badge>
                </Avatar>
                <Box>
                  <Typography variant="h6">{messageThreads.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Conversations
                  </Typography>
                  {totalUnreadMessages > 0 && (
                    <Typography variant="caption" color="error.main">
                      {totalUnreadMessages} unread messages
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <Badge badgeContent={pendingMeetings} color="error">
                    <Schedule />
                  </Badge>
                </Avatar>
                <Box>
                  <Typography variant="h6">{meetings.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Meeting Requests
                  </Typography>
                  {pendingMeetings > 0 && (
                    <Typography variant="caption" color="warning.main">
                      {pendingMeetings} pending approval
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <Badge badgeContent={draftAnnouncements} color="error">
                    <Notifications />
                  </Badge>
                </Avatar>
                <Box>
                  <Typography variant="h6">{announcements.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Announcements
                  </Typography>
                  {draftAnnouncements > 0 && (
                    <Typography variant="caption" color="info.main">
                      {draftAnnouncements} draft announcements
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Message />
                  Messages
                  {totalUnreadMessages > 0 && (
                    <Chip label={totalUnreadMessages} color="error" size="small" />
                  )}
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule />
                  Meetings
                  {pendingMeetings > 0 && (
                    <Chip label={pendingMeetings} color="warning" size="small" />
                  )}
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Notifications />
                  Announcements
                </Box>
              } 
            />
          </Tabs>
        </Box>

        <CardContent sx={{ p: 0 }}>
          {/* Messages Tab */}
          {activeTab === 0 && (
            <Box>
              {messageThreads.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    No messages yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    When parents send you messages, they will appear here.
                  </Typography>
                  <Button variant="contained" startIcon={<Add />}>
                    Send First Message
                  </Button>
                </Box>
              ) : (
                <List>
                  {messageThreads.map((thread, index) => (
                    <React.Fragment key={thread.id}>
                      <ListItem 
                        button 
                        onClick={() => handleThreadClick(thread.id)}
                        sx={{ 
                          py: 2,
                          '&:hover': { bgcolor: 'grey.50' }
                        }}
                      >
                        <ListItemAvatar>
                          <Badge badgeContent={thread.unreadCount} color="error">
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {thread.parentName.charAt(0)}
                            </Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: thread.unreadCount > 0 ? 600 : 400 }}>
                                {thread.parentName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                • {thread.childName}
                              </Typography>
                              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                                <Chip 
                                  label={thread.priority} 
                                  color={getPriorityColor(thread.priority) as any}
                                  size="small" 
                                />
                                <Chip 
                                  label={thread.status} 
                                  color={getStatusColor(thread.status) as any}
                                  size="small" 
                                />
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                {thread.subject}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}
                              >
                                {thread.lastMessage}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {thread.lastActivity}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < messageThreads.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Meetings Tab */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Meeting Requests
              </Typography>
              {meetings.map((meeting) => (
                <Card key={meeting.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {meeting.parentName} • {meeting.childName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {meeting.topic}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(meeting.requestedDate).toLocaleString()} • {meeting.duration}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip 
                          label={meeting.status} 
                          color={meeting.status === 'pending' ? 'warning' : 'success'}
                          size="small" 
                        />
                        {meeting.status === 'pending' && (
                          <>
                            <Button size="small" variant="outlined" color="error">
                              Decline
                            </Button>
                            <Button size="small" variant="contained">
                              Accept
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Announcements Tab */}
          {activeTab === 2 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  Class Announcements
                </Typography>
                <Button variant="contained" startIcon={<Add />}>
                  New Announcement
                </Button>
              </Box>
              {announcements.map((announcement) => (
                <Card key={announcement.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {announcement.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip 
                          label={announcement.priority} 
                          color={announcement.priority === 'high' ? 'warning' : 'default'}
                          size="small" 
                        />
                        <Chip 
                          label={announcement.status} 
                          color={announcement.status === 'sent' ? 'success' : 'default'}
                          size="small" 
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {announcement.content}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Target: {announcement.targetAudience} • Scheduled: {announcement.scheduledDate}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default TeacherMessages;
